/**
 * An inline SVG QR code.
 *
 * Rendered entirely in the browser — no image service, no external request.
 * That matters here beyond privacy: the thing being encoded is a single-use
 * token that grants adding a passkey to an account, so it must never be handed
 * to a third-party URL to draw.
 *
 * `qrcode-generator` was chosen over `qrcode` because it has ZERO dependencies;
 * `qrcode` pulls in yargs and pngjs for its CLI, none of which belongs in a
 * browser bundle.
 *
 * Drawn as one SVG `path` of module-sized rects rather than a grid of elements
 * — a typical URL is ~40×40 modules, and 1,600 DOM nodes to draw a square is a
 * waste React has to diff on every render.
 */

import { useMemo } from "react";
import qrcode from "qrcode-generator";

export function QrCode({
  value,
  size = 200,
  label,
}: {
  value: string;
  /** Rendered edge length in CSS pixels. */
  size?: number;
  /** Accessible description — the QR itself is invisible to a screen reader. */
  label?: string;
}) {
  const { path, count } = useMemo(() => {
    // typeNumber 0 = pick the smallest version that fits the data.
    // 'M' (~15% recovery) is the usual scanning-from-a-screen compromise:
    // enough redundancy for glare, not so much that the modules shrink.
    const qr = qrcode(0, "M");
    qr.addData(value);
    qr.make();

    const moduleCount = qr.getModuleCount();
    let d = "";
    for (let row = 0; row < moduleCount; row++) {
      for (let col = 0; col < moduleCount; col++) {
        if (qr.isDark(row, col)) {
          d += `M${col} ${row}h1v1h-1z`;
        }
      }
    }
    return { path: d, count: moduleCount };
  }, [value]);

  // 4 modules of quiet zone each side — required by the spec; without it many
  // scanners simply never lock on.
  const quiet = 4;
  const extent = count + quiet * 2;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${extent} ${extent}`}
      role="img"
      aria-label={label ?? "QR code"}
      shapeRendering="crispEdges"
      style={{
        // The quiet zone only works if it is actually light, so the background
        // is explicit rather than inherited from a possibly-dark card.
        background: "#fff",
        borderRadius: 8,
        display: "block",
      }}
    >
      <path d={path} fill="#000" transform={`translate(${quiet} ${quiet})`} />
    </svg>
  );
}
